/**
 * questDetail.ts — the quest-detail popover controller (QNAME-04, D-05/D-09/D-10/D-11/D-12/D-13,
 * 276-UI-SPEC.md's Detail Popover Contract).
 *
 * The ONE module that fetches, caches and renders a single quest's lazy detail body — chapter +
 * group, subtitle, task list, reward summary — behind a `.mc-quest-detail-trigger` span, on
 * BOTH surfaces that emit that trigger (the Activity Feed narrative and the Quests board's
 * Chapters/Tasks/Rewards sections, plan 276-06's own markup). Mirrors `forensicsPanel.ts`'s
 * established shape almost line-for-line, one layer simpler (a single quest object, not two item
 * groups + XP + coordinates).
 *
 * Two rules govern every line of this file:
 *
 * 1. Every payload-derived string is written via `textContent`/`createElement` — NEVER
 *    `innerHTML`. This docblock line is REQUIRED and must NOT be deleted or reworded to dodge a
 *    grep — every `innerHTML` gate this plan writes is comment-filtered precisely so this line can
 *    stay. If a gate ever reads non-zero, the defect is in executable code, never in this
 *    docblock. Enforced by literal grep (T-276-29), extending `forensicsPanel.ts:11`'s identical
 *    rule to the first Bootstrap `Popover` in this codebase — quest/task/chapter/reward titles are
 *    wire- and pack-sourced, attacker-adjacent strings, the same threat class as forensics item
 *    names. `html: true` on the Popover option below exists ONLY so Bootstrap accepts a DOM `Node`
 *    as `content` — no HTML string is ever built anywhere in this file.
 * 2. `Popover` is imported by name from the already-installed `bootstrap` package, never
 *    `window.bootstrap` — the standing project rule every Tooltip/Modal call site already follows.
 *    Zero new npm dependencies: `Popover` ships inside the same `bootstrap` 5.3.8 module `Tooltip`
 *    is already imported from (`src/js/mc.ts:28`).
 *
 *    The import itself is SPLIT deliberately, unlike `mc.ts`'s own top-level
 *    `import * as bootstrap from 'bootstrap'`: a `type`-only import here (erased entirely at
 *    runtime by both `tsc` and Node's native `.ts` type-stripping — zero bytes of executable code)
 *    plus a `const { Popover } = await import('bootstrap')` scoped INSIDE
 *    `attachQuestDetailTriggers()` below, the one function that actually constructs a `Popover`.
 *    This is a real, discovered environmental constraint, not a style preference: bootstrap's
 *    bundle touches `document`/`addEventListener` at MODULE-EVALUATION time (its own dismiss-
 *    trigger auto-registration), which crashes under `tests/mc/questDetail.test.ts`'s
 *    deliberately DOM-free harness (this repo ships no jsdom — zero new deps milestone-wide,
 *    matching `mcKeys.ts`'s own "ZERO imports" rationale for the identical reason). A static
 *    value-level import at the top of this file would make `node tests/mc/questDetail.test.ts`
 *    crash on load for every export, including the ones (`buildQuestDetailBody`,
 *    `QUEST_UNRESOLVED_TITLE`, the copy constants) that have nothing to do with Bootstrap at all.
 *    Deferring the VALUE import to the one call site that needs it — never evaluated by the
 *    test, which only exercises `buildQuestDetailBody()` — keeps the rest of this module
 *    importable and testable while the real `new Popover(...)` construction still uses the named
 *    export, never `window.bootstrap`.
 */

import type { Popover as PopoverType } from 'bootstrap';

// ---------------------------------------------------------------------------
// Copy constants (276-UI-SPEC.md's Detail Popover Contract / Copywriting Contract)
// ---------------------------------------------------------------------------

export const QUEST_DETAIL_LOADING_TEXT = 'Loading…';
export const QUEST_DETAIL_ERROR_TEXT = "Couldn't load quest detail.";

/**
 * Byte-identical mirror of `quests.latte`'s `{define questUnresolvedMarker}` `title="…"` copy
 * (D-26's honest-data phrase). A deliberate server/client mirror pair — the same divergence scar
 * `src/js/utils/mcKeys.ts`'s own docblock names for `humanizeMcKey()`/`McHumanize::minecraftKey()`
 * applies here too: an unverified copy is dangerous precisely because nothing forces the two
 * strings to move together. `tests/mc/questDetail.test.ts` reads `quests.latte` directly off disk
 * and asserts this constant is byte-identical to what it finds there — if either side is edited,
 * that test catches the drift.
 */
export const QUEST_UNRESOLVED_TITLE
    = "This id's title hasn't resolved from the pack's config tree. The id shown is real, greppable data — never fabricated.";

// ---------------------------------------------------------------------------
// Payload types — App\Utils\McQuestDetailMap::detailFor()'s return shape, camelCased on receipt
// (fetchQuestDetail() below does the one-time snake_case -> camelCase transform; nothing else in
// this file ever reads the raw wire keys).
// ---------------------------------------------------------------------------

/** One half of the chapter/chapter-group caption line — resolved title, or an id to fall back to
 *  (rendered as `<code>` + the unresolved marker) when `title` is `null`. The RED-scaffold unit
 *  test (`tests/mc/questDetail.test.ts`) exercises the plain-string shorthand below instead —
 *  both are accepted so the module works identically whether given a pre-resolved caption
 *  (test fixture) or a real id/title pair (production wire payload). */
export type QuestDetailHalf = string | { id: string; title: string | null };

export interface QuestDetailTask {
    id?: string;
    type: string;
    title: string | null;
}

export interface QuestDetailReward {
    type: string;
    label: string;
}

/**
 * The shape `buildQuestDetailBody()` consumes. `questId`/`questTitle` are deliberately OPTIONAL —
 * `App\Utils\McQuestDetailMap::detailFor()`'s wire payload carries no quest-level name/id field at
 * all (a quest's own resolved name is never duplicated into its own detail record), so the header
 * is populated by `renderQuestDetailInto()` below from the TRIGGER's own already-rendered DOM
 * content, never from a second name lookup (there is no client-side quest-name map to look one up
 * from — only the cold detail map, fetched here). Absent in the unit-test fixture, which does not
 * exercise header rendering.
 */
export interface QuestDetail {
    questId?: string;
    questTitle?: string | null;
    subtitle: string | null;
    chapter: QuestDetailHalf;
    chapterGroup: QuestDetailHalf;
    tasks: QuestDetailTask[];
    rewards: QuestDetailReward[];
}

/** The honest "no detail for this id" response — `McPresenter::handleGetQuestDetail()` answers a
 *  well-formed but out-of-scope/nonexistent quest id with this shape at HTTP 200, never a 404. In
 *  practice this should be unreachable (the trigger only renders when `has_detail` is already
 *  true), but the client still handles it honestly rather than assuming the flag was correct. */
interface QuestDetailAbsentPayload {
    has_detail: false;
}

interface QuestDetailWirePayload {
    subtitle: string | null;
    chapter: { id: string; title: string | null };
    chapter_group: { id: string; title: string | null };
    tasks: QuestDetailTask[];
    rewards: QuestDetailReward[];
}

type QuestDetailResponse = QuestDetailWirePayload | QuestDetailAbsentPayload;

function isAbsentPayload(value: unknown): value is QuestDetailAbsentPayload {
    return (
        typeof value === 'object'
        && value !== null
        && (value as { has_detail?: unknown }).has_detail === false
    );
}

// ---------------------------------------------------------------------------
// Per-id response cache — SUCCESS ONLY (deliberate deviation from forensicsPanel.ts's own
// cache-everything shape, see fetchQuestDetail()'s own docblock) + in-flight dedup + per-id
// stale-response guard, ported from forensicsPanel.ts:129-208.
// ---------------------------------------------------------------------------

const responseCache = new Map<string, QuestDetail | null>();
const inFlight = new Map<string, Promise<QuestDetail | null>>();
const requestSeq = new Map<string, number>();

function bumpRequestSeq(questId: string): number {
    const seq = (requestSeq.get(questId) ?? 0) + 1;
    requestSeq.set(questId, seq);
    return seq;
}

function currentRequestSeq(questId: string): number {
    return requestSeq.get(questId) ?? 0;
}

/**
 * Fetches (or returns the cached) detail body for one quest object id.
 *
 * DELIBERATE DEVIATION from `forensicsPanel.ts:fetchForensics()`'s own cache-everything shape:
 * only a SUCCESSFUL response is cached. A failed fetch (network error, non-ok HTTP, or the honest
 * `has_detail:false` answer) leaves the id UNCACHED, so the next open re-issues the request — that
 * IS the retry. 276-UI-SPEC.md's Detail Popover Contract explicitly rejects a visible "Try again"
 * control (a retry link inside a hover-dismissed popover forces the pointer across dead space,
 * dismissing the thing it lives in); re-hovering/re-focusing the trigger is the recovery gesture a
 * user already performs by reflex, and this cache policy is what makes that gesture actually work.
 * Do not add a retry affordance and do not change this to cache-on-any-outcome.
 */
export async function fetchQuestDetail(questId: string): Promise<QuestDetail | null> {
    if (responseCache.has(questId)) {
        return responseCache.get(questId) ?? null;
    }

    const pending = inFlight.get(questId);
    if (pending) {
        return pending;
    }

    const promise = (async (): Promise<QuestDetail | null> => {
        const url = `?do=getQuestDetail&id=${encodeURIComponent(questId)}`;
        try {
            const resp = await fetch(url, {
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
            });

            if (!resp.ok) {
                console.error('[mc-quest-detail] Quest detail fetch failed:', resp.status);
                return null;
            }

            const raw: QuestDetailResponse = await resp.json();
            if (isAbsentPayload(raw)) {
                return null;
            }

            return {
                subtitle: raw.subtitle,
                chapter: raw.chapter,
                chapterGroup: raw.chapter_group,
                tasks: Array.isArray(raw.tasks) ? raw.tasks : [],
                rewards: Array.isArray(raw.rewards) ? raw.rewards : [],
            };
        } catch (err) {
            console.error('[mc-quest-detail] Quest detail fetch error:', err);
            return null;
        }
    })();

    inFlight.set(questId, promise);

    try {
        const result = await promise;
        // SUCCESS-ONLY cache (see docblock above) — a null result is never written here, so the
        // next fetchQuestDetail() call for this id re-issues the request rather than returning a
        // cached miss forever.
        if (result !== null) {
            responseCache.set(questId, result);
        }
        return result;
    } finally {
        inFlight.delete(questId);
    }
}

// ---------------------------------------------------------------------------
// Item-reward mechanical humanizer — the ONLY branch reachable client-side (D-14/D-05: this
// module has no McTranslationMap counterpart, and this phase adds no third generated map).
// Mirrors App\Mc\McHumanize::namespacedItemName()'s mechanical fallback branch, mechanically —
// the same branch 370 of 679 real item rewards (54.5%) already land on server-side, the MAJORITY
// case, not a defect (see the module-level note in McQuestDetailMap.php's own docblock and
// 276-08-PLAN.md's own worked example). The 20.6% McTranslationMap-hit tier is NOT reachable here
// — those labels also degrade to this mechanical shape client-side, an accepted, documented
// tradeoff of the hot/cold split (D-05) rather than a bug.
//
// ASCII-only ucwords, matching mcKeys.ts's own documented PHP toupper()-is-byte-wise contract
// (`créate` stays `Créate`, never `CréAte`).
// ---------------------------------------------------------------------------

function ucwordsAscii(s: string): string {
    return s.replace(/(^|[ \t\r\n\f\v])([a-z])/g, (_m, sep: string, c: string) => sep + c.toUpperCase());
}

function humanizeRawItemId(raw: string): string {
    const colonIndex = raw.indexOf(':');
    const namespace = colonIndex > 0 ? raw.slice(0, colonIndex) : 'minecraft';
    const bare = colonIndex > 0 ? raw.slice(colonIndex + 1) : raw;
    const label = ucwordsAscii(bare.replace(/_/g, ' '));
    return namespace === 'minecraft' ? label : `${label} (${namespace})`;
}

/** Item reward labels arrive from the wire as `"{count}× {raw_namespaced_id}"` (D-14 — the map
 *  bakes only the raw id, deferring humanization to this client-side mechanical tier). A label
 *  that does not match this shape renders verbatim rather than throwing — defensive, never a live
 *  path today. */
const ITEM_REWARD_LABEL_RE = /^(\d+)× (.+)$/;

function humanizeItemRewardLabel(rawLabel: string): string {
    const match = ITEM_REWARD_LABEL_RE.exec(rawLabel);
    if (match === null) {
        return rawLabel;
    }
    const [, count, rawId] = match;
    return `${count}× ${humanizeRawItemId(rawId)}`;
}

// ---------------------------------------------------------------------------
// buildQuestDetailBody() — the loaded-state body (276-UI-SPEC.md's Popover Body Structure)
// ---------------------------------------------------------------------------

function buildUnresolvedMarker(): HTMLElement {
    const marker = document.createElement('span');
    marker.className = 'mc-quest-unresolved-marker';
    marker.title = QUEST_UNRESOLVED_TITLE;
    marker.textContent = 'unresolved';
    return marker;
}

/** Appends one resolved-title-or-raw-id+marker half to `parent` — the same name-resolution rule
 *  used everywhere else in this phase. A plain string (the unit-test fixture's shorthand, and any
 *  already-resolved caller-supplied caption) renders as-is with no fallback machinery. */
function appendHalf(parent: HTMLElement, value: QuestDetailHalf): void {
    if (typeof value === 'string') {
        parent.appendChild(document.createTextNode(value));
        return;
    }
    if (value.title !== null) {
        parent.appendChild(document.createTextNode(value.title));
        return;
    }
    const code = document.createElement('code');
    code.textContent = value.id;
    parent.appendChild(code);
    parent.appendChild(document.createTextNode(' '));
    parent.appendChild(buildUnresolvedMarker());
}

function buildTaskRow(task: QuestDetailTask): HTMLElement {
    const row = document.createElement('li');
    row.className = 'mc-command-row';

    const name = document.createElement('span');
    name.className = 'mc-command-name';
    if (task.title !== null) {
        name.textContent = task.title;
    } else if (task.id !== undefined) {
        const code = document.createElement('code');
        code.textContent = task.id;
        name.appendChild(code);
        name.appendChild(document.createTextNode(' '));
        name.appendChild(buildUnresolvedMarker());
    } else {
        // Defensive-only: title null AND no id present never occurs against the real map (every
        // real task entry carries an id). Renders the honest unresolved marker with no fabricated
        // id rather than a blank row.
        name.appendChild(buildUnresolvedMarker());
    }
    row.appendChild(name);

    return row;
}

function buildRewardRow(reward: QuestDetailReward): HTMLElement {
    const row = document.createElement('li');
    row.className = 'mc-command-row';

    const name = document.createElement('span');
    name.className = 'mc-command-name';
    if (reward.type === 'item') {
        name.textContent = humanizeItemRewardLabel(reward.label);
    } else if (reward.type === 'advancement') {
        // No client-side advancement_title lookup exists (D-05 — zero new maps this phase), so
        // the honest miss path is the ONLY reachable path here: raw id, mono, no fabricated title.
        const code = document.createElement('code');
        code.textContent = reward.label;
        name.appendChild(code);
    } else {
        // xp / xp_levels / custom / choice / random — the map already bakes the final display
        // label for every one of these (D-13's neutral labels verbatim for custom/choice/random).
        name.textContent = reward.label;
    }
    row.appendChild(name);

    return row;
}

function buildListSection(headingText: string, rows: HTMLElement[], emptyText: string): HTMLElement {
    const section = document.createElement('div');
    section.className = 'mb-2';

    const heading = document.createElement('p');
    heading.className = 'text-muted small mb-1';
    heading.textContent = headingText;
    section.appendChild(heading);

    const scroll = document.createElement('div');
    scroll.className = 'mc-quest-section-scroll';

    const list = document.createElement('ul');
    list.className = 'mc-command-list list-unstyled';

    if (rows.length === 0) {
        const emptyRow = document.createElement('li');
        emptyRow.className = 'mc-command-row';
        const emptyName = document.createElement('span');
        emptyName.className = 'mc-command-name text-muted';
        emptyName.textContent = emptyText;
        emptyRow.appendChild(emptyName);
        list.appendChild(emptyRow);
    } else {
        for (const row of rows) {
            list.appendChild(row);
        }
    }

    scroll.appendChild(list);
    section.appendChild(scroll);
    return section;
}

/**
 * Builds the loaded popover body: chapter + group caption line, subtitle (only when present —
 * never a "No subtitle" placeholder, D-26/276-UI-SPEC.md), Tasks section, Rewards section — in
 * that exact order. Every node is `createElement`/`textContent`/`createTextNode` only.
 *
 * The quest's OWN header (resolved name or raw id + marker) is deliberately NOT built here — see
 * `QuestDetail`'s own docblock for why (no name/id field exists on the wire payload this function
 * consumes). `renderQuestDetailInto()` below prepends the header separately, cloned from the
 * trigger's own already-rendered DOM content.
 */
export function buildQuestDetailBody(detail: QuestDetail): Node {
    const root = document.createElement('div');

    const caption = document.createElement('p');
    caption.className = 'text-muted small mb-2';
    appendHalf(caption, detail.chapter);
    caption.appendChild(document.createTextNode(' · '));
    appendHalf(caption, detail.chapterGroup);
    root.appendChild(caption);

    if (detail.subtitle !== null && detail.subtitle !== undefined) {
        const subtitle = document.createElement('p');
        subtitle.className = 'text-muted';
        subtitle.textContent = detail.subtitle;
        root.appendChild(subtitle);
    }

    const taskRows = detail.tasks.map(buildTaskRow);
    root.appendChild(buildListSection(`Tasks — ${detail.tasks.length}`, taskRows, 'No tasks defined.'));

    const rewardRows = detail.rewards.map(buildRewardRow);
    root.appendChild(buildListSection(`Rewards — ${detail.rewards.length}`, rewardRows, 'No rewards defined.'));

    return root;
}

// ---------------------------------------------------------------------------
// renderQuestDetailInto() — the fetch-then-fill shell each popover's content node calls
// ---------------------------------------------------------------------------

/**
 * Clears `container`, shows the loading copy, awaits `fetchQuestDetail()`, then replaces the
 * container's children with the built header + body — or the error copy on a `null` result.
 * Guards against a stale response for this SAME quest id overwriting a newer render of the SAME
 * container via the per-id sequence counter declared above (mirrors `forensicsPanel.ts`'s own
 * `renderInto()`).
 *
 * `headerNodes` — cloned directly from the trigger's own already-rendered child nodes (its
 * resolved name, or its raw-id+marker fallback) by `attachQuestDetailTriggers()` below. Passing
 * the SAME DOM subtree the trigger itself renders, rather than re-deriving a name from a second
 * lookup, keeps the popover header and the trigger's own visible label impossible to drift apart
 * — there is only one source of truth, the server-rendered/JS-built trigger element.
 */
export async function renderQuestDetailInto(
    container: HTMLElement,
    questId: string,
    headerNodes: Node[] = [],
): Promise<void> {
    const seq = bumpRequestSeq(questId);

    container.replaceChildren();

    if (headerNodes.length > 0) {
        const header = document.createElement('div');
        header.className = 'fw-semibold mb-2';
        for (const node of headerNodes) {
            header.appendChild(node);
        }
        container.appendChild(header);
    }

    const loading = document.createElement('p');
    loading.className = 'text-muted small mb-0';
    loading.textContent = QUEST_DETAIL_LOADING_TEXT;
    container.appendChild(loading);

    const detail = await fetchQuestDetail(questId);

    // A newer renderQuestDetailInto() call for this SAME quest id has already superseded this
    // one — never write a stale result over a newer render.
    if (currentRequestSeq(questId) !== seq) {
        return;
    }

    loading.remove();

    if (detail === null) {
        const error = document.createElement('p');
        error.className = 'text-muted small mb-0';
        error.textContent = QUEST_DETAIL_ERROR_TEXT;
        container.appendChild(error);
        return;
    }

    container.appendChild(buildQuestDetailBody(detail));
}

// ---------------------------------------------------------------------------
// attachQuestDetailTriggers() / initQuestDetail() — the idempotent init pass (D-09)
// ---------------------------------------------------------------------------

const TRIGGER_SELECTOR = '.mc-quest-detail-trigger[data-quest-detail-id]';

/** Idempotency ledger (D-09) — mirrors `waystoneSort.ts`'s own `controllers` WeakMap idiom, sized
 *  here as a `WeakSet` since this module tracks presence only, never per-trigger state. */
const questDetailBoundTriggers = new WeakSet<HTMLElement>();

/**
 * Binds a `Popover` to every `.mc-quest-detail-trigger[data-quest-detail-id]` under `root` that
 * does not already have one. Idempotency is MANDATORY, not a nicety (this repo has a documented
 * history of double-binding shared components that initialise on every page) — this function is
 * safe to call repeatedly, including on the same element, which is what makes plan 276-09's
 * per-append call (for feed rows appended after the initial page load) safe.
 *
 * `trigger: 'hover focus'` + `html: true` (Bootstrap accepts a DOM `Node` as `content` only when
 * this is set — see the file-level docblock: no HTML string is ever built regardless). Content
 * fetches lazily on `show.bs.popover`, not at init time (D-10) — and on EVERY show, not just the
 * first: `fetchQuestDetail()`'s success-only cache makes a repeat call a cache hit after a
 * successful load, or the correct retry after a failed one (see that function's own docblock).
 */
export async function attachQuestDetailTriggers(root: ParentNode = document): Promise<void> {
    const triggers = Array.from(root.querySelectorAll<HTMLElement>(TRIGGER_SELECTOR));

    // Nothing to bind — skip the dynamic import entirely rather than paying for loading Bootstrap
    // on a page with zero triggers (e.g. every non-Quests/non-Feed `/mc` page `initQuestDetail()`
    // itself already no-ops on, but a direct `attachQuestDetailTriggers()` call from plan 276-09's
    // per-append path could still be invoked with an empty root).
    const pending = triggers.filter((el) => !questDetailBoundTriggers.has(el));
    if (pending.length === 0) {
        return;
    }

    // See the file-level docblock (rule 2) for why this import is HERE, dynamically, rather than
    // a static top-of-file value import: `Popover` is imported by name, never `window.bootstrap`.
    // Explicit `: { Popover: typeof PopoverType }` gives the module-scope `type`-only import a
    // real use — a signature check that the dynamically-imported binding still shapes up the way
    // the rest of this function assumes, not just an inert docblock reference.
    //
    // Phase 276 Plan 09 discovery: the SAME specifier resolves to two different shapes depending
    // on who resolves it. rspack (the real bundled/shipped path, `npm run build:prod`) honors
    // `bootstrap`'s package.json `module` field — the real ES module (`bootstrap.esm.js`), which
    // exports `Popover` as a genuine named export, so `bootstrapNs.Popover` is set directly there.
    // Plain Node module resolution (this repo's own jsdom-free `node file.ts` test harness, e.g.
    // `tests/mc/questDetail.test.ts`) has no `module`-field concept and instead resolves the
    // package's CJS `main` build (`bootstrap.js`, a UMD bundle whose only top-level statement is
    // `module.exports = index_umd`) — Node's CJS-ESM interop can't statically detect named exports
    // from a single dynamic assignment like that, so the awaited namespace carries only
    // `default`/`module.exports`, each holding the whole `index_umd` object. The `?? ...default?.`
    // fallback below makes both resolution paths work from the exact same source line — confirmed
    // functionally equivalent in the bundled output (no new chunk file after this change, same as
    // 276-08's own split-import verification) and is what makes this function callable end-to-end
    // from a real test.
    const bootstrapNs: { Popover?: typeof PopoverType; default?: { Popover: typeof PopoverType } }
        = await import('bootstrap');
    const Popover: typeof PopoverType | undefined = bootstrapNs.Popover ?? bootstrapNs.default?.Popover;
    if (Popover === undefined) {
        // Defensive-only: neither resolution shape produced a usable class. Never silently no-op
        // triggers a viewer can see but that would never open — surfaced as a thrown error so it
        // fails loudly during development rather than shipping an inert cue.
        throw new Error('[mc-quest-detail] attachQuestDetailTriggers(): Popover class not found on the bootstrap module');
    }

    for (const el of pending) {
        // Re-check under the real Bootstrap instance registry too (belt-and-suspenders): a caller
        // could have raced two attachQuestDetailTriggers() calls before either's dynamic import
        // resolved. Popover.getInstance(el) is the authoritative idempotency source; the
        // Set above only exists to avoid re-querying Bootstrap's registry for every element on
        // every call when nothing changed.
        if (Popover.getInstance(el)) {
            questDetailBoundTriggers.add(el);
            continue;
        }

        const questId = el.dataset.questDetailId;
        if (questId === undefined || questId === '') {
            continue;
        }

        const content = document.createElement('div');

        // `sanitize`/`allowList` are left at Bootstrap's own defaults, deliberately unset here:
        // TemplateFactory only runs its HTML sanitizer on STRING content
        // (node_modules/bootstrap/js/dist/util/template-factory.js's own `_setContent()` —
        // an Element `content` value is moved into the template directly, bypassing
        // `_maybeSanitize()` entirely). Passing a Node is what makes this safe, not a sanitizer
        // option; do not add `sanitize: false` here — that would misleadingly read as this file
        // opting OUT of a protection it was never depending on in the first place.
        new Popover(el, {
            trigger: 'hover focus',
            customClass: 'mc-quest-detail-popover',
            html: true,
            content,
            container: 'body',
        });

        el.addEventListener('show.bs.popover', () => {
            const headerNodes = Array.from(el.childNodes).map((node) => node.cloneNode(true));
            void renderQuestDetailInto(content, questId, headerNodes);
        });

        questDetailBoundTriggers.add(el);
    }
}

let questDetailInited = false;

/**
 * Page-entry function — self-guards on the presence of at least one trigger AND on a module-level
 * double-init guard, following `initWaystoneSort()`'s idiom (`src/js/mc/waystoneSort.ts:55-59`).
 * Called unconditionally from `mc.ts`'s own `static init()`, matching every other `/mc` submodule.
 *
 * Deliberately synchronous (`void`) at the call site even though `attachQuestDetailTriggers()` is
 * async — `mc.ts`'s `static init()` calls every `initX()` unconditionally and synchronously, and
 * this module's own dynamic `import('bootstrap')` resolving a tick later costs nothing: no other
 * code depends on triggers being bound before `init()` returns. Errors are still surfaced (never
 * silently dropped) via `.catch()`.
 */
export function initQuestDetail(): void {
    if (questDetailInited) {
        return;
    }
    if (document.querySelector(TRIGGER_SELECTOR) === null) {
        return;
    }
    questDetailInited = true;
    attachQuestDetailTriggers(document).catch((err: unknown) => {
        console.error('[mc-quest-detail] initQuestDetail() failed:', err);
    });
}
